import { DateTime } from "luxon";
import { useState } from "react";
import { Link } from "react-router-dom";
import Loader from "../../../common/Loader";
import SaveBtn from "../../../common/SaveBtn";
import EPARList from "../ARList/EPARList";
import Jury from "../Jury/Jury";
import EPCancelledInfo from "./components/EPCancelledInfo";
import EPFormFields from "./components/EPFormFields";
import EpRecipientsV2 from "./EpRecipientsV2/EpRecipientsV2";
import { useEPEditDetailV2 } from "./hooks/useEPEditDetailV2";

const EPModalV2 = ({
  ep,
  saveEp,
  removeEp,
  errors,
  setErrors,
  isSavingEp,
  isRemovingEp,
  sectors,
  onRefreshCalendar,
  onClose,
}) => {
  const {
    myEp,
    setMyEp,
    epRecipients,
    isGettingRecipients,
    allSectors,
    jury,
    juryDepsFetched,
    fetchJuryDatas,
    allMembers,
  } = useEPEditDetailV2(ep, setErrors);

  const [activeTab, setActiveTab] = useState("details");

  const nbWeeksBeforeEp = DateTime.fromISO(myEp.beginAt)
    .diff(DateTime.local(), "weeks")
    .toObject().weeks;

  const is5WeeksBeforeEp = nbWeeksBeforeEp >= 5;
  const isDisabled =
    myEp.id && (myEp.closedAt || !is5WeeksBeforeEp) ? true : false;

  const getStatusBadge = () => {
    if (ep.cancelledAt) return { text: "Annulée", color: "default" };
    if (ep.endedAt) return { text: "Finalisée", color: "dark" };
    if (ep.closedAt) return { text: "Clôturée", color: "success" };
    if (ep.startedAt) return { text: "Démarrée", color: "info" };
    return { text: "Planifiée", color: "primary" };
  };

  const status = getStatusBadge();

  const tabs = [
    { id: "details", label: "Détails", icon: "fa-info-circle" },
    {
      id: "jury",
      label: "Jury",
      icon: "fa-users",
      badge: jury.length,
      disabled: !ep.id,
    },
    {
      id: "recipients",
      label: "Allocataires",
      icon: "fa-address-book",
      badge: epRecipients.length,
      disabled: !ep.id,
    },
    {
      id: "ar",
      label: "État des courriers",
      icon: "fa-envelope",
      disabled: !ep.id,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "details":
        return (
          <div className="row">
            {ep.isFull && (
              <div className="col-12 mb-3">
                <div className="alert alert-warning mb-0">
                  <i className="fa fa-lock mr-2" />
                  <strong>Capacité maximale atteinte :</strong> Cet EP a atteint
                  le maximum de {ep.parametres.nbDossierMaxParEP} allocataires
                  inscrits.
                </div>
              </div>
            )}
            <EPFormFields
              myEp={myEp}
              setMyEp={setMyEp}
              errors={errors}
              isDisabled={isDisabled}
              sectors={sectors}
              allSectors={allSectors}
            />
            <EPCancelledInfo ep={ep} />
            {!ep.cancelledAt && (
              <div className="col-12 mt-3">
                <div className="d-flex justify-content-between">
                  <SaveBtn
                    isSaving={isSavingEp}
                    className="btn-lg"
                    text="Enregistrer les modifications"
                    save={() => saveEp(myEp)}
                  />
                  {!isDisabled && (
                    <SaveBtn
                      type="btn-link"
                      className="btn text-danger"
                      isSaving={isRemovingEp}
                      save={() => removeEp(myEp)}
                      text="Annuler cet EP"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case "jury":
        return juryDepsFetched && allMembers?.userManagers ? (
          <Jury
            ep={ep}
            myjury={jury}
            allMembers={allMembers}
            sectors={allSectors}
            fetchJuryDatas={fetchJuryDatas}
          />
        ) : (
          <Loader />
        );
      case "recipients":
        return isGettingRecipients ? (
          <Loader />
        ) : (
          <EpRecipientsV2 ep={ep} onRefreshCalendar={onRefreshCalendar} />
        );
      case "ar":
        return <EPARList epData={ep} />;
      default:
        return null;
    }
  };

  return (
    <div
      className="container-fluid h-100 d-flex flex-column"
      style={{ maxWidth: 1400 }}
    >
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h2
                className="mb-2"
                style={{ fontSize: 28, fontWeight: 600, color: "#2c3e50" }}
              >
                {ep.id ? "Gestion de l'EP" : "Nouvelle EP"}
                <span
                  className={`badge badge-${status.color} ml-3`}
                  style={{ fontSize: 14 }}
                >
                  {status.text}
                </span>
              </h2>
              <div className="text-muted" style={{ fontSize: 14 }}>
                <i className="fa fa-calendar mr-2" />
                {DateTime.fromISO(myEp.beginAt)
                  .setLocale("fr")
                  .toFormat("EEEE dd MMMM yyyy 'à' HH'h'mm")}
                <span className="mx-2">•</span>
                <i className="fa fa-map-marker mr-2" />
                {sectors.find((s) => s.id === myEp.sector)?.name ||
                  "Secteur non défini"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="row mb-3">
        <div className="col-12">
          <ul
            className="nav nav-tabs nav-justified"
            style={{ borderBottom: "2px solid #e0e0e0" }}
          >
            {tabs.map((tab) => (
              <li key={tab.id} className={tab.disabled ? "disabled" : ""}>
                <a
                  href="#"
                  className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!tab.disabled) setActiveTab(tab.id);
                  }}
                  style={{
                    fontSize: 16,
                    fontWeight: 500,
                    padding: "12px 20px",
                    color:
                      activeTab === tab.id
                        ? "#007bff"
                        : tab.disabled
                          ? "#ccc"
                          : "#666",
                    borderBottom:
                      activeTab === tab.id ? "3px solid #007bff" : "none",
                    cursor: tab.disabled ? "not-allowed" : "pointer",
                    opacity: tab.disabled ? 0.5 : 1,
                  }}
                >
                  <i className={`fa ${tab.icon} mr-2`} />
                  {tab.label}
                  {tab.badge !== undefined && (
                    <span
                      className={`badge badge-${tab.badge > 0 ? "success" : "secondary"} ml-2`}
                      style={{ fontSize: 12 }}
                    >
                      {tab.badge}
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="row flex-grow-1" style={{ overflowY: "auto" }}>
        <div className="col-12">
          <div className="bg-white p-4 rounded" style={{ minHeight: 400 }}>
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      {ep.id && !ep.cancelledAt && (
        <div
          className="row mt-4 pt-3"
          style={{ borderTop: "1px solid #e0e0e0" }}
        >
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                {ep.startedAt && (
                  <div className="text-muted" style={{ fontSize: 14 }}>
                    <i className="fa fa-clock-o mr-2" />
                    Démarrée le{" "}
                    {DateTime.fromISO(ep.startedAt)
                      .setLocale("fr")
                      .toFormat("dd MMMM yyyy 'à' HH'h'mm")}
                  </div>
                )}
              </div>
              <div>
                {/* <span>Démarrage de l'EP en cours de dev...</span> */}
                <Link
                  className={`btn btn-${ep.startedAt ? "info" : "success"} btn-lg`}
                  onClick={onClose}
                  to={`/ep-v2/${ep.id}`}
                  style={{ fontSize: 16, padding: "10px 30px" }}
                >
                  <i
                    className={`fa fa-${ep.startedAt ? "arrow-right" : "play"} mr-2`}
                  />
                  {ep.startedAt
                    ? ep.closedAt
                      ? ep.endedAt
                        ? "Consulter"
                        : "Ouvrir"
                      : "Rejoindre"
                    : "Démarrer"}{" "}
                  cet EP
                </Link>
                {ep.closedAt && !ep.endedAt && (
                  <Link
                    to={`/ep-v2/${ep.id}`}
                    onClick={onClose}
                    className="btn btn-warning btn-lg ml-2"
                    style={{ fontSize: 16, padding: "10px 30px" }}
                  >
                    <i className="fa fa-gavel mr-2" />
                    Validation Directeur
                  </Link>
                )}
                {ep.endedAt && (
                  <Link
                    to={`ep-v2/${ep.id}/pv`}
                    onClick={onClose}
                    className="btn btn-primary btn-lg ml-2"
                    style={{ fontSize: 16, padding: "10px 30px" }}
                  >
                    <i className="fa fa-file-text mr-2" />
                    Procès Verbal
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EPModalV2;
